import { displayText } from '../../../../../terminology/store';
import { useTerminology } from '../../../../../terminology/TerminologyContext';
import React, { useContext, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import ThemeContext from '../../../../ThemeContext';
import AppConfig from '../../../../../config/applicationconfig';
import { URL_SEGMENTS } from '../../../../../config/Routesconfig';
import {
  getCheckedFoldersKey,
  getCheckedDocumentsKey,
} from '../../../../Utils/storageKeys';
import {
  getDeposition,
  getCase,
  getCaseFolders,
  createCaseFolder,
  updateCaseFolder,
  deleteCaseFolder,
  getFolderDocuments,
  uploadDocuments,
  createPlaceholder,
  updateDocument,
  deleteDocument,
  moveDocument,
  copyDocument,
  downloadDocument,
  downloadFolder,
  copyCaseFolder,
  ensureCsrfToken,
  deleteDeposition,
  deleteArbitration,
  deleteTrial,
  deleteMediation,
  deleteWitnessPrep,
  deleteCustomSession,
} from '../../../../services/api';
import api from '../../../../services/api';

import ClientConfig from '../../../../../config/Clientconfig';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
const user = JSON.parse(localStorage.getItem("user"));
const userRole = user?.role;
const isClientUser = userRole === "client_user";

const ViewDeposition = () => {
  useTerminology();
  const { theme } = useContext(ThemeContext);
  const { username, caseId, sessionId } = useParams();
  const navigate = useNavigate();

  // Typography from theme
  const fontFamily =
    theme?.typography?.fontFamily ||
    '"Roboto", "Helvetica Neue", Arial, sans-serif';
  const titleFontSize = theme?.typography?.titleFontSize || 24;
  const bodyFontSize = theme?.typography?.bodyFontSize || 14;
  const smallFontSize = Math.max(bodyFontSize - 2, 10);
  const xSmallFontSize = Math.max(bodyFontSize - 4, 8);

  /* ------------------- STATE ------------------- */
  const [depositionData, setDepositionData] = useState(null);
  const [caseData, setCaseData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [checkedFolders, setCheckedFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [checkedDocuments, setCheckedDocuments] = useState([]);
  const [sortBy, setSortBy] = useState('custom');
  const [showSortMenu, setShowSortMenu] = useState(false);
  const [emptyRowCount] = useState(20);
  
  /* ------------------- MODALS ------------------- */
  const [showAddFolderModal, setShowAddFolderModal] = useState(false);
  const [showRenameFolderModal, setShowRenameFolderModal] = useState(false);
  const [folderToEdit, setFolderToEdit] = useState(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [showPlaceholderModal, setShowPlaceholderModal] = useState(false);
  const [placeholderName, setPlaceholderName] = useState('');
  const [showRenameDocModal, setShowRenameDocModal] = useState(false);
  const [docToRename, setDocToRename] = useState(null);
  const [newDocName, setNewDocName] = useState('');
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [targetFolder, setTargetFolder] = useState(null);

  /* ------------------- STORAGE KEYS ------------------- */
  const FOLDER_KEY = getCheckedFoldersKey(caseId, sessionId);
  const getDocumentKey = (folderId) =>
    getCheckedDocumentsKey(folderId, sessionId);

  /* --------------------------------------------------------------- */
  /* Load / Save checked folders */
  /* --------------------------------------------------------------- */
  useEffect(() => {
    const saved = localStorage.getItem(FOLDER_KEY);
    if (saved) setCheckedFolders(JSON.parse(saved));
  }, [FOLDER_KEY]);

  useEffect(() => {
    if (checkedFolders.length > 0) {
      localStorage.setItem(FOLDER_KEY, JSON.stringify(checkedFolders));
    } else {
      localStorage.removeItem(FOLDER_KEY);
    }
  }, [checkedFolders, FOLDER_KEY]);

  /* --------------------------------------------------------------- */
  /* Load / Save checked documents (per folder) */
  /* --------------------------------------------------------------- */
  useEffect(() => {
    if (!selectedFolder?.id) return;
    const docKey = getDocumentKey(selectedFolder.id);
    const saved = localStorage.getItem(docKey);
    if (saved) {
      const parsed = JSON.parse(saved);
      setCheckedDocuments(parsed);
    } else {
      setCheckedDocuments([]);
    }
  }, [selectedFolder?.id]);

  useEffect(() => {
    if (!selectedFolder?.id) return;
    const docKey = getDocumentKey(selectedFolder.id);
    if (checkedDocuments.length > 0) {
      localStorage.setItem(docKey, JSON.stringify(checkedDocuments));
    } else {
      localStorage.removeItem(docKey);
    }
  }, [checkedDocuments, selectedFolder?.id]);

  /* --------------------------------------------------------------- */
  /* Data fetching */
  /* --------------------------------------------------------------- */
  useEffect(() => {
    fetchData();
    fetchFolders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId, sessionId]);

  useEffect(() => {
    if (selectedFolder?.id) {
      fetchDocuments(selectedFolder.id);
    } else {
      setDocuments([]);
      setCheckedDocuments([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFolder?.id]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [caseResponse, depositionResponse] = await Promise.all([
        getCase(caseId),
        getDeposition(sessionId),
      ]);
      setCaseData(caseResponse.data.data);
      setDepositionData(depositionResponse.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(displayText('Failed to fetch deposition details.'), {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      navigate(`/${username}/dashboard/case/${caseId}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchFolders = async () => {
    try {
      const response = await getCaseFolders(caseId, sessionId);
      const folderList = response.data || [];
      setFolders(folderList);
      if (!selectedFolder && folderList.length > 0) {
        const defaultFolder =
          folderList.find((f) => f.is_default) || folderList[0];
        setSelectedFolder(defaultFolder);
      }
      const saved = localStorage.getItem(FOLDER_KEY);
      if (saved) {
        const savedIds = JSON.parse(saved);
        setCheckedFolders((prev) => {
          return folderList
            .filter((f) => savedIds.includes(f.id))
            .map((f) => f.id);
        });
      }
    } catch (error) {
      console.error('Failed to fetch folders:', error);
      toast.error('Failed to load folders', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
    }
  };

  const fetchDocuments = async (folderId) => {
    try {
      const response = await getFolderDocuments(folderId);
      const docs = response.data || [];
      setDocuments(docs);
      const docKey = getDocumentKey(folderId);
      const saved = localStorage.getItem(docKey);
      if (saved) {
        const savedIds = JSON.parse(saved);
        setCheckedDocuments((prev) => {
          return docs
            .filter((d) => savedIds.includes(d.id))
            .map((d) => d.id);
        });
      } else {
        setCheckedDocuments([]);
      }
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      toast.error('Failed to load documents', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      setDocuments([]);
      setCheckedDocuments([]);
    }
  };

  /* --------------------------------------------------------------- */
  /* Deposition actions */
  /* --------------------------------------------------------------- */
  const handleDeleteSession = async () => {
  if (!window.confirm(displayText('Are you sure you want to delete this session?'))) return;

  try {
    setIsLoading(true);

    const type = depositionData?.session_type;

    switch (type) {
      case 'deposition':
        await deleteDeposition(sessionId);
        break;

      case 'arbitration':
        await deleteArbitration(sessionId);
        break;

      case 'trial':
        await deleteTrial(sessionId);
        break;

      case 'mediation':
        await deleteMediation(sessionId);
        break;

      case 'witness_prep':
        await deleteWitnessPrep(sessionId);
        break;

      case 'custom':
  await deleteCustomSession(caseId, sessionId);
  break;

      default:
        throw new Error(`Unsupported session type: ${type}`);
    }

    toast.success(`${type || displayText('Session')} deleted successfully.`, {
      toastStyle: {
        backgroundColor: theme.colors.bgPrimary,
        border: `1px solid ${theme.colors.borderColor}`,
        color: theme.colors.success,
        fontFamily,
        fontSize: `${bodyFontSize}px`,
      },
    });

    navigate(`/${username}/dashboard/case/${caseId}`);
  } catch (error) {
    toast.error(
      `${displayText("Failed to delete session: ")}${error.response?.data?.message || error.message}${displayText("")}`,
      {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      }
    );
  } finally {
    setIsLoading(false);
  }
};

  /* --------------------------------------------------------------- */
  /* Folder row click & selection */
  /* --------------------------------------------------------------- */
  const handleFolderRowClick = (folder, e) => {
    if (
      e.target.closest('input[type="checkbox"]') ||
      e.target.closest('button')
    )
      return;
    setSelectedFolder(folder);
  };

  /* --------------------------------------------------------------- */
  /* Folder checkbox toggle */
  /* --------------------------------------------------------------- */
  const toggleFolderChecked = (folderId, e) => {
    e.stopPropagation();
    setCheckedFolders((prev) => {
      const updated = prev.includes(folderId)
        ? prev.filter((id) => id !== folderId)
        : [...prev, folderId];
      if (updated.length > 0) {
        localStorage.setItem(FOLDER_KEY, JSON.stringify(updated));
      } else {
        localStorage.removeItem(FOLDER_KEY);
      }
      return updated;
    });
  };

  /* --------------------------------------------------------------- */
  /* Select-All Folders */
  /* --------------------------------------------------------------- */
  const handleSelectAllFolders = () => {
    const selectable = folders.filter((f) => !f.is_default).map((f) => f.id);
    const updated =
      checkedFolders.length === selectable.length ? [] : selectable;
    setCheckedFolders(updated);
    if (updated.length > 0) {
      localStorage.setItem(FOLDER_KEY, JSON.stringify(updated));
    } else {
      localStorage.removeItem(FOLDER_KEY);
    }
  };

  /* --------------------------------------------------------------- */
  /* Document checkbox toggle */
  /* --------------------------------------------------------------- */
  const toggleDocumentChecked = (docId, e) => {
    e.stopPropagation();
    setCheckedDocuments((prev) => {
      const updated = prev.includes(docId)
        ? prev.filter((id) => id !== docId)
        : [...prev, docId];
      const docKey = getDocumentKey(selectedFolder.id);
      if (updated.length > 0) {
        localStorage.setItem(docKey, JSON.stringify(updated));
      } else {
        localStorage.removeItem(docKey);
      }
      return updated;
    });
  };

  /* --------------------------------------------------------------- */
  /* Select-All Documents */
  /* --------------------------------------------------------------- */
  const handleSelectAllDocuments = () => {
    const allIds = documents.map((d) => d.id);
    const updated =
      checkedDocuments.length === allIds.length ? [] : allIds;
    setCheckedDocuments(updated);
    const docKey = getDocumentKey(selectedFolder.id);
    if (updated.length > 0) {
      localStorage.setItem(docKey, JSON.stringify(updated));
    } else {
      localStorage.removeItem(docKey);
    }
  };

  /* --------------------------------------------------------------- */
  /* Folder CRUD */
  /* --------------------------------------------------------------- */
  const handleAddFolder = () => {
    setNewFolderName('');
    setShowAddFolderModal(true);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName?.trim()) {
      toast.warning('Please enter a folder name', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      return;
    }
    try {
      await createCaseFolder(caseId, {
  name: newFolderName.trim(),
  session_id: sessionId,
});
      toast.success('Folder created successfully', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      fetchFolders();
      setShowAddFolderModal(false);
      setNewFolderName('');
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to create folder',
        {
          toastStyle: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.danger,
            fontFamily,
            fontSize: `${bodyFontSize}px`,
          },
        }
      );
    }
  };

  const handleRenameFolder = (folder, e) => {
    e.stopPropagation();
    setFolderToEdit(folder);
    setNewFolderName(folder.name);
    setShowRenameFolderModal(true);
  };

  const handleUpdateFolder = async () => {
    if (!newFolderName?.trim() || newFolderName === folderToEdit.name) {
      setShowRenameFolderModal(false);
      return;
    }
    try {
      await updateCaseFolder(folderToEdit.id, {
        name: newFolderName.trim(),
      });
      toast.success('Folder renamed successfully', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      fetchFolders();
      setShowRenameFolderModal(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to rename folder',
        {
          toastStyle: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.danger,
            fontFamily,
            fontSize: `${bodyFontSize}px`,
          },
        }
      );
    }
  };

  const handleDeleteFolder = async () => {
    if (checkedFolders.length === 0) {
      toast.warning('Please select folders to delete', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      return;
    }
    if (!window.confirm(`Delete ${checkedFolders.length} folder(s)?`)) return;
    try {
      for (const folderId of checkedFolders) {
        await deleteCaseFolder(folderId);
      }
      toast.success('Folders deleted successfully', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      setCheckedFolders([]);
      localStorage.removeItem(FOLDER_KEY);
      fetchFolders();
    } catch (error) {
      toast.error('Failed to delete folders', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
    }
  };

  const handleCopyFolders = async () => {
    if (checkedFolders.length === 0) {
      toast.warning('Please select folders to copy', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      return;
    }
    try {
      for (const folderId of checkedFolders) {
        await copyCaseFolder(folderId);
      }
      toast.success('Folders copied successfully', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      fetchFolders();
    } catch (error) {
      toast.error('Failed to copy folders', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
    }
  };

  const handleDownloadFolders = async () => {
    if (checkedFolders.length === 0) {
      toast.warning('Please select folders to download', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      return;
    }
    for (const folderId of checkedFolders) {
      try {
        const folder = folders.find((f) => f.id === folderId);
        const response = await downloadFolder(folderId);
        if (response.data instanceof Blob) {
          const blob = new Blob([response.data], {
            type: 'application/zip',
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${folder.name}_${
            new Date().toISOString().split('T')[0]
          }.zip`;
          document.body.appendChild(link);
          setTimeout(() => link.click(), 100);
          setTimeout(() => {
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }, 600);
          toast.success(`${folder.name} download started`, {
            toastStyle: {
              backgroundColor: theme.colors.bgPrimary,
              border: `1px solid ${theme.colors.borderColor}`,
              color: theme.colors.success,
              fontFamily,
              fontSize: `${bodyFontSize}px`,
            },
          });
        }
      } catch (error) {
        toast.error('Failed to download folder', {
          toastStyle: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.danger,
            fontFamily,
            fontSize: `${bodyFontSize}px`,
          },
        });
      }
    }
  };

  /* --------------------------------------------------------------- */
  /* Document sorting & helpers */
  /* --------------------------------------------------------------- */
  const getSortedDocuments = () => {
    const sorted = [...documents];
    switch (sortBy) {
      case 'name':
        return sorted.sort((a, b) => a.name.localeCompare(b.name));
      case 'date':
        return sorted.sort(
          (a, b) =>
            new Date(b.created_at).getTime() -
            new Date(a.created_at).getTime()
        );
      default:
        return sorted;
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleDownloadDocument = async (doc, e) => {
    e.stopPropagation();
    try {
      const response = await downloadDocument(doc.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Document downloaded', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
    } catch (error) {
      toast.error('Failed to download document', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
    }
  };

  const handleViewDocument = (doc, e) => {
    e.stopPropagation();

    if (doc.is_placeholder) {
      toast.info('This is a placeholder — no file to view.', {
        toastStyle: { /* your existing style */ }
      });
      return;
    }

    // Prepare payload for Presentation component
    const payload = {
      docId: doc.id,
      docName: doc.name || doc.title,
      sessionId: sessionId,           // <-- important: matches your route param
    };

    // Store in localStorage so Presentation can read it
    try {
      localStorage.setItem(`presentation_${sessionId}`, JSON.stringify(payload));
    } catch (err) {
      console.error('Failed to store presentation payload', err);
    }

    // Navigate to Presentation page
    navigate('/presentation', { state: payload });
  };

  /* --------------------------------------------------------------- */
  /* ----------  UPLOAD DOCUMENTS  ---------- */
  /* --------------------------------------------------------------- */
  const handleUploadDocuments = async () => {
    if (selectedFiles.length === 0) {
      toast.warning('Please select files to upload', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      return;
    }

    const formData = new FormData();

selectedFiles.forEach((file) => {
  formData.append('files[]', file);
});

    try {
      await ensureCsrfToken();
      await uploadDocuments(selectedFolder.id, formData);

      toast.success('Documents uploaded successfully!', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      await fetchDocuments(selectedFolder.id);
      setShowUploadModal(false);
      setSelectedFiles([]);
    } catch (error) {
      console.error('Upload error:', error);
      const errMsg = error.response?.data?.message || error.message;
      const errors = error.response?.data?.errors;
      if (errors) {
        const flat = Object.values(errors).flat().join(' | ');
        toast.error(`Upload failed: ${flat}`, {
          toastStyle: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.danger,
            fontFamily,
            fontSize: `${bodyFontSize}px`,
          },
        });
      } else {
        toast.error(`Upload failed: ${errMsg}`, {
          toastStyle: {
            backgroundColor: theme.colors.bgPrimary,
            border: `1px solid ${theme.colors.borderColor}`,
            color: theme.colors.danger,
            fontFamily,
            fontSize: `${bodyFontSize}px`,
          },
        });
      }
    }
  };

  const handleCreatePlaceholder = async () => {
    if (!placeholderName.trim()) {
      toast.warning('Please enter a placeholder name', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      return;
    }
    try {
      await createPlaceholder(selectedFolder.id, {
        name: placeholderName,
      });
      toast.success('Placeholder created', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      fetchDocuments(selectedFolder.id);
      setShowPlaceholderModal(false);
      setPlaceholderName('');
    } catch (error) {
      toast.error('Failed to create placeholder', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
    }
  };

  const handleRenameDocument = (doc, e) => {
    e.stopPropagation();
    setDocToRename(doc);
    setNewDocName(doc.name);
    setShowRenameDocModal(true);
  };

  const handleUpdateDocument = async () => {
    if (!newDocName.trim() || newDocName === docToRename.name) {
      setShowRenameDocModal(false);
      return;
    }
    try {
      await updateDocument(docToRename.id, { name: newDocName });
      toast.success('Document renamed', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      fetchDocuments(selectedFolder.id);
      setShowRenameDocModal(false);
    } catch (error) {
      toast.error('Failed to rename document', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
    }
  };

  const handleCopyDocuments = async () => {
    if (checkedDocuments.length === 0 || !targetFolder) {
      toast.warning('Please select documents and target folder', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      return;
    }
    try {
      for (const id of checkedDocuments) {
        await copyDocument(id, { target_folder_id: targetFolder.id });
      }
      toast.success('Documents copied', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      setShowCopyModal(false);
      setTargetFolder(null);
    } catch (error) {
      toast.error('Failed to copy documents', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
    }
  };

  const handleMoveDocuments = async () => {
    if (checkedDocuments.length === 0 || !targetFolder) {
      toast.warning('Please select documents and target folder', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      return;
    }
    try {
      for (const id of checkedDocuments) {
        await moveDocument(id, { target_folder_id: targetFolder.id });
      }
      toast.success('Documents moved', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      fetchDocuments(selectedFolder.id);
      setCheckedDocuments([]);
      localStorage.removeItem(getDocumentKey(selectedFolder.id));
      setShowMoveModal(false);
      setTargetFolder(null);
    } catch (error) {
      toast.error('Failed to move documents', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
    }
  };

  const handleDeleteDocuments = async () => {
    if (checkedDocuments.length === 0) {
      toast.warning('Please select documents to delete', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      return;
    }
    if (!window.confirm(`Delete ${checkedDocuments.length} document(s)?`))
      return;
    try {
      for (const id of checkedDocuments) {
        await deleteDocument(id);
      }
      toast.success('Documents deleted', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.success,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      fetchDocuments(selectedFolder.id);
      setCheckedDocuments([]);
      localStorage.removeItem(getDocumentKey(selectedFolder.id));
    } catch (error) {
      toast.error('Failed to delete documents', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.danger,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
    }
  };

  const handleDownloadSelected = async () => {
    if (checkedDocuments.length === 0) {
      toast.warning('Please select documents to download', {
        toastStyle: {
          backgroundColor: theme.colors.bgPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          color: theme.colors.warning,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        },
      });
      return;
    }
    for (const id of checkedDocuments) {
      const doc = documents.find((d) => d.id === id);
      if (!doc || doc.is_placeholder) continue;
      await handleDownloadDocument(doc, { stopPropagation: () => {} });
    }
  };

  const getFileIcon = (doc) => {
    if (doc.is_placeholder) {
      return {
        icon: "bi-file-earmark-plus",
        color: theme.colors.warning,
      };
    }

    const name = (doc.name || "").toLowerCase();
    const ext = name.split(".").pop() || "";

    switch (ext) {
      case "pdf":
        return { icon: "bi-file-earmark-pdf", color: theme.colors.danger };

      case "doc":
      case "docx":
        return { icon: "bi-file-earmark-word", color: "#2b579a" };

      case "xls":
      case "xlsx":
      case "xlsm":
      case "csv":
        return { icon: "bi-file-earmark-spreadsheet", color: "#217346" };

      case "ppt":
      case "pptx":
        return { icon: "bi-filetype-ppt", color: "#d24726" };

      case "jpg":
      case "jpeg":
        return { icon: "bi-filetype-jpg", color: theme.colors.primary };

      case "png":
        return { icon: "bi-filetype-png", color: theme.colors.primary };

      case "gif":
        return { icon: "bi-filetype-gif", color: theme.colors.primary };

      case "txt":
        return { icon: "bi-file-earmark-text", color: theme.colors.textSecondary };

      case "zip":
      case "rar":
        return { icon: "bi-file-earmark-zip", color: theme.colors.warning };

      default:
        return { icon: "bi-file-earmark", color: theme.colors.textSecondary };
    }
  };

  /* --------------------------------------------------------------- */
  /* Render */
  /* --------------------------------------------------------------- */
  if (isLoading && !depositionData) {
    return (
      <div
        style={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        }}
      >
        <div
          style={{
            width: '40px',
            height: '40px',
            border: `4px solid ${theme.colors.primary}`,
            borderTop: '4px solid transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); }}`}</style>
      </div>
    );
  }
   
  if (!depositionData) {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: '3rem',
        backgroundColor: theme.colors.bgPrimary,
        color: theme.colors.textPrimary,
        fontFamily,
        fontSize: `${bodyFontSize}px`,
      }}
    >{displayText(" No session data available. ")}</div>
  );
}

  const getDisplaySessionType = (session) => {
  const type = (session?.session_type || '').toLowerCase().trim();

  switch (type) {
    case 'deposition':
      return AppConfig.deposition;
    case 'arbitration':
      return AppConfig.arbiration;
    case 'trial':
      return AppConfig.trail;
    case 'mediation':
      return AppConfig.mediation;
    case 'witness_prep':
      return AppConfig.witness;
    case 'custom':
      return session?.custom_type?.trim() || 'Custom Session';
    default:
      return session?.session_type || 'Session';
  }
};

const displaySessionType = getDisplaySessionType(depositionData);

  const sortedDocuments = getSortedDocuments();

  return (
    <div
      style={{
        fontFamily,
        fontSize: `${bodyFontSize}px`,
        minHeight: '100vh',
        padding: '1.5rem',
        backgroundColor: theme.colors.bgPrimary,
        color: theme.colors.textPrimary,
      }}
    >
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
        toastStyle={{
          backgroundColor: theme.colors.bgPrimary,
          color: theme.colors.textPrimary,
          border: `1px solid ${theme.colors.borderColor}`,
          fontFamily,
          fontSize: `${bodyFontSize}px`,
        }}
      />

      {/* ---------- Modals ---------- */}
      {showAddFolderModal && (
        <div
          className="modal fade show"
          style={{
            display: 'block',
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1050,
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{
                backgroundColor: theme.colors.bgPrimary,
                color: theme.colors.textPrimary,
                border: `1px solid ${theme.colors.borderColor}`,
                fontFamily,
              }}
            >
              <div
                className="modal-header"
                style={{
                  borderBottom: `1px solid ${theme.colors.borderColor}`,
                }}
              >
                <h5
                  className="modal-title"
                  style={{ fontSize: `${smallFontSize}px` }}
                >
                  {/*  label from ClientConfig */}
                  {ClientConfig.folders.addLabel}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddFolderModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="form-control"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Enter folder name"
                  style={{ fontSize: `${smallFontSize}px`, fontFamily }}
                />
              </div>
              <div
                className="modal-footer"
                style={{
                  borderTop: `1px solid ${theme.colors.borderColor}`,
                }}
              >
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowAddFolderModal(false)}
                  style={{
                    fontSize: `${xSmallFontSize}px`,
                    padding: '5px 10px',
                    fontFamily,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreateFolder}
                  style={{
                    fontSize: `${xSmallFontSize}px`,
                    padding: '5px 10px',
                    fontFamily,
                  }}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showUploadModal && (
        <div
          className="modal fade show"
          style={{
            display: 'block',
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1050,
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{
                backgroundColor: theme.colors.bgPrimary,
                color: theme.colors.textPrimary,
                border: `1px solid ${theme.colors.borderColor}`,
                fontFamily,
              }}
            >
              <div
                className="modal-header"
                style={{
                  borderBottom: `1px solid ${theme.colors.borderColor}`,
                }}
              >
                <h5
                  className="modal-title"
                  style={{ fontSize: `${smallFontSize}px` }}
                >
                  Upload Documents
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFiles([]);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <input
                  type="file"
                  multiple
                  onChange={(e) =>
                    setSelectedFiles(Array.from(e.target.files))
                  }
                  className="form-control"
                  style={{ fontSize: `${smallFontSize}px`, fontFamily }}
                />
                {selectedFiles.length > 0 && (
                  <div
                    style={{
                      marginTop: '0.5rem',
                      fontSize: `${xSmallFontSize}px`,
                    }}
                  >
                    <strong>Selected ({selectedFiles.length}):</strong>
                    <ul
                      style={{
                        margin: '0.25rem 0',
                        paddingLeft: '1rem',
                      }}
                    >
                      {selectedFiles.map((f, i) => (
                        <li key={i}>
                          {f.name} ({formatFileSize(f.size)})
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div
                className="modal-footer"
                style={{
                  borderTop: `1px solid ${theme.colors.borderColor}`,
                }}
              >
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowUploadModal(false);
                    setSelectedFiles([]);
                  }}
                  style={{
                    fontSize: `${xSmallFontSize}px`,
                    padding: '5px 10px',
                    fontFamily,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUploadDocuments}
                  disabled={selectedFiles.length === 0}
                  style={{
                    fontSize: `${xSmallFontSize}px`,
                    padding: '5px 10px',
                    fontFamily,
                  }}
                >
                  Upload
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showPlaceholderModal && (
        <div
          className="modal fade show"
          style={{
            display: 'block',
            backgroundColor: 'rgba(0,0,0,0.5)',
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1050,
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div
              className="modal-content"
              style={{
                backgroundColor: theme.colors.bgPrimary,
                color: theme.colors.textPrimary,
                border: `1px solid ${theme.colors.borderColor}`,
                fontFamily,
              }}
            >
              <div
                className="modal-header"
                style={{
                  borderBottom: `1px solid ${theme.colors.borderColor}`,
                }}
              >
                <h5
                  className="modal-title"
                  style={{ fontSize: `${smallFontSize}px` }}
                >
                  {/*  label from ClientConfig */}
                  {ClientConfig.documents.addPlaceholderLabel}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowPlaceholderModal(false);
                    setPlaceholderName('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="form-control"
                  value={placeholderName}
                  onChange={(e) => setPlaceholderName(e.target.value)}
                  placeholder="Placeholder name"
                  style={{ fontSize: `${smallFontSize}px`, fontFamily }}
                />
              </div>
              <div
                className="modal-footer"
                style={{
                  borderTop: `1px solid ${theme.colors.borderColor}`,
                }}
              >
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowPlaceholderModal(false);
                    setPlaceholderName('');
                  }}
                  style={{
                    fontSize: `${xSmallFontSize}px`,
                    padding: '5px 10px',
                    fontFamily,
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleCreatePlaceholder}
                  style={{
                    fontSize: `${xSmallFontSize}px`,
                    padding: '5px 10px',
                    fontFamily,
                  }}
                >
                  Create
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* HEADER */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '1rem',
          padding: '6px',
          borderRadius: '6px',
          border: `1px solid ${theme.colors.borderColor}`,
          backgroundColor: theme.colors.bgPrimary,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}
        >
          <i
            className="bi bi-folder-fill"
            style={{ fontSize: `${smallFontSize}px`, color: theme.colors.primary }}
          ></i>
          <span
  onClick={() => navigate(`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`)}
  style={{
    fontSize: `${xSmallFontSize}px`,
    color: theme.colors.textPrimary,
    fontWeight: 'bold',
    textDecoration: 'none',
    cursor: 'pointer',
    fontFamily,
  }}
>{displayText(" Case Management / ")}{caseData?.case_name || displayText('Case')}
</span>
          <span
  style={{
    fontSize: `${xSmallFontSize}px`,
    fontWeight: 'bold',
  }}
>
  / {displaySessionType}: {depositionData.name || 'Unnamed'}
</span>
        </div>
        <button
  onClick={() => navigate(`/${username}/dashboard/${URL_SEGMENTS.viewCase}/${caseId}`)}
          style={{
            background: theme.gradients.secondary,
            color: theme.colors.textWhite,
            fontSize: `${smallFontSize}px`,
            padding: '8px 12px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            fontFamily,
          }}
        >
          {/*  label from ClientConfig */}
          {ClientConfig.sessions.backToCaseLabel}
        </button>
      </div>

      {/* Session Options */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          marginBottom: '4px',
        }}
      >
        {!isClientUser && (
        <div className="dropdown">
          <button
            className="btn dropdown-toggle"
            type="button"
            data-bs-toggle="dropdown"
            style={{
              background: theme.gradients.primary,
              color: theme.colors.textWhite,
              fontSize: `${smallFontSize}px`,
              padding: '8px 12px',
              borderRadius: '8px',
              border: 'none',
              fontFamily,
            }}
          >
            {/*  label from ClientConfig */}
            {ClientConfig.sessions.sessionOptionsLabel}
          </button>
          <ul
            className="dropdown-menu"
            style={{
              backgroundColor: theme.colors.bgPrimary,
              border: `1px solid ${theme.colors.borderColor}`,
              fontSize: `${xSmallFontSize}px`,
              fontFamily,
            }}
          >
            <li>
              <Link
                className="dropdown-item"
                to={`/${username}/dashboard/case/${caseId}/edit-deposition/${sessionId}`}
                style={{ color: theme.colors.textPrimary, fontFamily }}
              >
                {/*  label from ClientConfig */}
                Edit {ClientConfig.sessions.depositionLabel}
              </Link>
            </li>
            <li>
              <button
                className="dropdown-item"
                onClick={handleDeleteSession}
                style={{ color: theme.colors.danger, fontFamily }}
              >
                {/*  label from ClientConfig */}
                Delete {ClientConfig.sessions.depositionLabel}
              </button>
            </li>
          </ul>
        </div>
            )}
      </div>

      {/* MAIN CONTAINER */}
      <div
        style={{
          padding: '2rem 1.5rem 1.5rem 1.5rem',
          borderRadius: '10px',
          padding: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            gap: '1rem',
            flexWrap: 'wrap',
          }}
        >
          {/* FOLDERS PANEL */}
          <div
            style={{
              flex: 1,
              minWidth: '300px',
              border: `1px solid ${theme.colors.borderColor}`,
              backgroundColor: theme.colors.bgSecondary,
            }}
          >
            <div
              style={{
                padding: '0.75rem',
                borderBottom: `1px solid ${theme.colors.borderColor}`,
                backgroundColor: theme.colors.bgPrimary,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <i
                  className="bi bi-folder-fill"
                  style={{ color: theme.colors.primary }}
                ></i>
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: `${smallFontSize}px`,
                  }}
                >
                  {ClientConfig.folders.pluralLabel} ({folders.length})
                </span>
              </div>
              {!isClientUser && (
  <button
    onClick={handleAddFolder}
    style={{
      background: theme.gradients.primary,
      color: theme.colors.textWhite,
      border: 'none',
      padding: '6px 10px',
      borderRadius: '5px',
      fontSize: `${smallFontSize}px`,
      cursor: 'pointer',
      fontFamily,
    }}
  >
    {ClientConfig.folders.addLabel}
  </button>
)}
</div>
            
            <div
              style={{
                height: 'calc(20 * 36px)',
                overflowY: 'auto',
                padding: '0.5rem',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '0.5rem',
                  marginBottom: '2px',
                  borderRadius: '4px',
                  backgroundColor: theme.colors.bgSecondary,
                  border: `1px solid ${theme.colors.borderColor}`,
                }}
              >
                {!isClientUser && (
  <input
    type="checkbox"
    checked={
      checkedFolders.length > 0 &&
      checkedFolders.length ===
        folders.filter((f) => !f.is_default).length
    }
    onChange={handleSelectAllFolders}
    style={{ marginRight: '0.5rem', cursor: 'pointer' }}
  />
)}
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: `${xSmallFontSize}px`,
                  }}
                >
                  {ClientConfig.folders.selectAllLabel}
                </span>
              </div>
              {folders.map((folder) => (
                <div
                  key={folder.id}
                  onClick={(e) => handleFolderRowClick(folder, e)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '0.5rem',
                    marginBottom: '2px',
                    borderRadius: '4px',
                    backgroundColor:
                      selectedFolder?.id === folder.id
                        ? '#ff9933'
                        : theme.colors.bgPrimary,
                    border: `1px solid ${theme.colors.borderColor}`,
                    cursor: 'pointer',
                    fontSize: `${xSmallFontSize}px`,
                  }}
                >
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      flex: 1,
                    }}
                  >
                    <input
  type="checkbox"
  checked={checkedFolders.includes(folder.id)}
  onChange={(e) => toggleFolderChecked(folder.id, e)}
  disabled={isClientUser}
  style={{ cursor: isClientUser ? "not-allowed" : "pointer" }}
/>
                    <i
                      className="bi bi-folder"
                      style={{ color: theme.colors.primary }}
                    ></i>
                    <span
                      style={{
                        color:
                          selectedFolder?.id === folder.id
                            ? '#fff'
                            : theme.colors.textPrimary,
                      }}
                    >
                      {folder.name}
                    </span>
                    {folder.is_default && (
                      <span
                        style={{
                          fontSize: `${xSmallFontSize}px`,
                          fontStyle: 'italic',
                          marginLeft: '0.25rem',
                        }}
                      >
                        ({ClientConfig.folders.defaultLabel})
                      </span>
                    )}
                  </div>
                  {!folder.is_default && (
                    <button
                      onClick={(e) => handleRenameFolder(folder, e)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color:
                          selectedFolder?.id === folder.id
                            ? '#fff'
                            : theme.colors.primary,
                        fontSize: `${xSmallFontSize}px`,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                      }}
                    >
                      Rename
                    </button>
                  )}
                </div>
              ))}
            </div>
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                padding: '0.75rem',
                borderTop: `1px solid ${theme.colors.borderColor}`,
              }}
            >
              <span
                style={{
                  fontSize: `${xSmallFontSize}px`,
                  alignSelf: 'center',
                }}
              >
                Selected: {checkedFolders.length}
              </span>
              <button
                onClick={handleCopyFolders}
                disabled={checkedFolders.length === 0}
                style={{
                  flex: 1,
                  padding: '6px',
                  fontSize: `${xSmallFontSize}px`,
                  border: 'none',
                  borderRadius: '4px',
                  cursor:
                    checkedFolders.length === 0 ? 'not-allowed' : 'pointer',
                  backgroundColor:
                    checkedFolders.length === 0
                      ? theme.colors.bgSecondary
                      : theme.colors.info,
                  color: theme.colors.textWhite,
                  opacity: checkedFolders.length === 0 ? 0.5 : 1,
                }}
              >
                Copy
              </button>
              <button
                onClick={handleDownloadFolders}
                disabled={checkedFolders.length === 0}
                style={{
                  flex: 1,
                  padding: '6px',
                  fontSize: `${xSmallFontSize}px`,
                  border: 'none',
                  borderRadius: '4px',
                  cursor:
                    checkedFolders.length === 0 ? 'not-allowed' : 'pointer',
                  backgroundColor:
                    checkedFolders.length === 0
                      ? theme.colors.bgSecondary
                      : theme.colors.success,
                  color: theme.colors.textWhite,
                  opacity: checkedFolders.length === 0 ? 0.5 : 1,
                }}
              >
                Download
              </button>
              <button
                onClick={handleDeleteFolder}
                disabled={checkedFolders.length === 0}
                style={{
                  flex: 1,
                  padding: '6px',
                  fontSize: `${xSmallFontSize}px`,
                  border: 'none',
                  borderRadius: '4px',
                  cursor:
                    checkedFolders.length === 0 ? 'not-allowed' : 'pointer',
                  backgroundColor:
                    checkedFolders.length === 0
                      ? theme.colors.bgSecondary
                      : theme.colors.danger,
                  color: theme.colors.textWhite,
                  opacity: checkedFolders.length === 0 ? 0.5 : 1,
                }}
              >
                Delete
              </button>
            </div>
          </div>

          {/* DOCUMENTS PANEL */}
          <div
            style={{
              flex: 1,
              minWidth: '300px',
              border: `1px solid ${theme.colors.borderColor}`,
              backgroundColor: theme.colors.bgSecondary,
            }}
          >
            <div
              style={{
                padding: '0.75rem',
                borderBottom: `1px solid ${theme.colors.borderColor}`,
                backgroundColor: theme.colors.bgPrimary,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                }}
              >
                <i
                  className="bi bi-file-earmark-text-fill"
                  style={{ color: theme.colors.primary }}
                ></i>
                <span
                  style={{
                    fontWeight: 'bold',
                    fontSize: `${smallFontSize}px`,
                  }}
                >
                  {ClientConfig.documents.pluralLabel} ({documents.length})
                </span>
                <div style={{ position: 'relative', marginLeft: '1rem' }}>
                  <button
                    onClick={() => setShowSortMenu(!showSortMenu)}
                    style={{
                      background: theme.colors.bgSecondary,
                      border: `1px solid ${theme.colors.borderColor}`,
                      padding: '6px 10px',
                      borderRadius: '5px',
                      fontSize: `${smallFontSize}px`,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.25rem',
                    }}
                  >
                    {sortBy === 'custom'
                      ? 'Custom'
                      : sortBy === 'date'
                      ? 'Date'
                      : 'Name'}
                    <i className="bi bi-chevron-down"></i>
                  </button>
                  {showSortMenu && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        marginTop: '0.25rem',
                        backgroundColor: theme.colors.bgPrimary,
                        border: `1px solid ${theme.colors.borderColor}`,
                        borderRadius: '5px',
                        zIndex: 1000,
                        minWidth: '120px',
                      }}
                    >
                      {['custom', 'date', 'name'].map((option) => (
                        <button
                          key={option}
                          onClick={() => {
                            setSortBy(option);
                            setShowSortMenu(false);
                          }}
                          style={{
                            width: '100%',
                            padding: '0.5rem',
                            fontSize: `${smallFontSize}px`,
                            border: 'none',
                            background:
                              sortBy === option
                                ? theme.colors.bgSecondary
                                : 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                          }}
                        >
                          {option.charAt(0).toUpperCase() +
                            option.slice(1)}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                {!isClientUser && (
    <>
                <button
                  onClick={() => setShowUploadModal(true)}
                  disabled={!selectedFolder}
                  style={{
                    background: !selectedFolder
                      ? theme.colors.bgSecondary
                      : theme.gradients.primary,
                    color: theme.colors.textWhite,
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '5px',
                    fontSize: `${smallFontSize}px`,
                    cursor: !selectedFolder ? 'not-allowed' : 'pointer',
                    opacity: !selectedFolder ? 0.5 : 1,
                    fontFamily,
                  }}
                >
                  {ClientConfig.documents.addLabel}
                </button>
                <button
                  onClick={() => setShowPlaceholderModal(true)}
                  disabled={!selectedFolder}
                  style={{
                    background: !selectedFolder
                      ? theme.colors.bgSecondary
                      : theme.gradients.secondary,
                    color: theme.colors.textWhite,
                    border: 'none',
                    padding: '6px 10px',
                    borderRadius: '5px',
                    fontSize: `${smallFontSize}px`,
                    cursor: !selectedFolder ? 'not-allowed' : 'pointer',
                    opacity: !selectedFolder ? 0.5 : 1,
                    fontFamily,
                  }}
                >
        {ClientConfig.documents.addPlaceholderLabel}
      </button>
    </>
  )}
  </div> 
</div>
            <div
              style={{
                maxHeight: 'calc(20 * 38px)',
                overflowY: 'auto',
                padding: '0.5rem',
              }}
            >
              {!selectedFolder ? (
                <div
                  style={{
                    textAlign: 'center',
                    padding: '1.5rem',
                    color: theme.colors.textSecondary,
                    fontSize: `${xSmallFontSize}px`,
                  }}
                >
                  {/*  label from ClientConfig */}
                  {ClientConfig.documents.selectFolderPrompt}
                </div>
              ) : (
                <table
                  style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: `${xSmallFontSize}px`,
                  }}
                >
                  <thead
                    style={{
                      position: 'sticky',
                      top: 0,
                      backgroundColor: theme.colors.bgPrimary,
                      zIndex: 1,
                    }}
                  >
                    <tr
                      style={{
                        borderBottom: `1px solid ${theme.colors.borderColor}`,
                      }}
                    >
                      <th
                        style={{
                          padding: '0.4rem',
                          textAlign: 'left',
                          width: '30px',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={
                            checkedDocuments.length === documents.length &&
                            documents.length > 0
                          }
                          onChange={handleSelectAllDocuments}
                        />
                      </th>
                      <th style={{ padding: '0.4rem', textAlign: 'left' }}>
                        Name
                      </th>
                      <th style={{ padding: '0.4rem', textAlign: 'left' }}>
                        Size
                      </th>
                      <th style={{ padding: '0.4rem', textAlign: 'left' }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {documents.length === 0 ? (
                      <tr>
                        <td
                          colSpan="4"
                          style={{
                            textAlign: 'center',
                            padding: '1.5rem',
                            color: theme.colors.textSecondary,
                          }}
                        >
                          {/*  label from ClientConfig */}
                          {ClientConfig.documents.emptyMessage}
                        </td>
                      </tr>
                    ) : (
                      sortedDocuments.map((doc) => {
                        const { icon, color } = getFileIcon(doc);

                        return (
                          <tr
                            key={doc.id}
                            onClick={(e) =>
                              e.target.closest('button, input') || toggleDocumentChecked(doc.id, e)
                            }
                            style={{
                              borderBottom: `1px solid ${theme.colors.borderColor}`,
                              backgroundColor: checkedDocuments.includes(doc.id)
                                ? theme.colors.bgSecondary
                                : theme.colors.bgPrimary,
                              cursor: 'pointer',
                            }}
                          >
                            <td>
  {!isClientUser && (
    <input
      type="checkbox"
      checked={checkedDocuments.includes(doc.id)}
      onChange={(e) => toggleDocumentChecked(doc.id, e)}
    />
  )}
</td>
                            <td style={{ padding: '0.4rem' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                <i className={icon} style={{ color }} />
                                <span>{doc.name}</span>
                              </div>
                            </td>
                            <td style={{ padding: '0.4rem' }}>
                              {doc.is_placeholder ? ClientConfig.documents.placeholderText : formatFileSize(doc.file_size)}
                            </td>
                            <td style={{ padding: '0.4rem' }}>
                              <div style={{ display: 'flex', gap: '0.5rem' }}>
                                {!doc.is_placeholder && (
                                  <button
                                    onClick={(e) => handleViewDocument(doc, e)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: theme.colors.primary,
                                      fontSize: `${xSmallFontSize}px`,
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    View
                                  </button>
                                )}
                                {!isClientUser && (
                                <button
                                  onClick={(e) => handleRenameDocument(doc, e)}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: theme.colors.primary,
                                    fontSize: `${xSmallFontSize}px`,
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                  }}
                                >
                                  Rename
                                </button>
                                )}
                                {!doc.is_placeholder && (
                                  <button
                                    onClick={(e) => handleDownloadDocument(doc, e)}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: theme.colors.success,
                                      fontSize: `${xSmallFontSize}px`,
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    Download
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                    {documents.length > 0 &&
                      documents.length < emptyRowCount &&
                      Array.from({
                        length: emptyRowCount - documents.length,
                      }).map((_, idx) => (
                        <tr
                          key={`empty-${idx}`}
                          style={{
                            borderBottom: `1px solid ${theme.colors.borderColor}`,
                          }}
                        >
                          <td style={{ padding: '0.4rem' }}>&nbsp;</td>
                          <td style={{ padding: '0.4rem' }}>&nbsp;</td>
                          <td style={{ padding: '0.4rem' }}>&nbsp;</td>
                          <td style={{ padding: '0.4rem' }}>&nbsp;</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              )}
            </div>
            <div
              style={{
                display: 'flex',
                gap: '0.5rem',
                padding: '0.75rem',
                borderTop: `1px solid ${theme.colors.borderColor}`,
              }}
            >
              <span
                style={{
                  fontSize: `${xSmallFontSize}px`,
                  alignSelf: 'center',
                }}
              >
                Selected ({checkedDocuments.length})
              </span>
              {!isClientUser && (
  <>

              <button
                onClick={() => setShowCopyModal(true)}
                disabled={checkedDocuments.length === 0}
                style={{
                  flex: 1,
                  padding: '6px',
                  fontSize: `${xSmallFontSize}px`,
                  border: 'none',
                  borderRadius: '4px',
                  cursor:
                    checkedDocuments.length === 0
                      ? 'not-allowed'
                      : 'pointer',
                  backgroundColor:
                    checkedDocuments.length === 0
                      ? theme.colors.bgSecondary
                      : theme.colors.info,
                  color: theme.colors.textWhite,
                  opacity: checkedDocuments.length === 0 ? 0.5 : 1,
                }}
              >
                Copy
              </button>
              <button
                onClick={handleDownloadSelected}
                disabled={checkedDocuments.length === 0}
                style={{
                  flex: 1,
                  padding: '6px',
                  fontSize: `${xSmallFontSize}px`,
                  border: 'none',
                  borderRadius: '4px',
                  cursor:
                    checkedDocuments.length === 0
                      ? 'not-allowed'
                      : 'pointer',
                  backgroundColor:
                    checkedDocuments.length === 0
                      ? theme.colors.bgSecondary
                      : theme.colors.success,
                  color: theme.colors.textWhite,
                  opacity: checkedDocuments.length === 0 ? 0.5 : 1,
                }}
              >
                Download
              </button>
              <button
                onClick={() => setShowMoveModal(true)}
                disabled={checkedDocuments.length === 0}
                style={{
                  flex: 1,
                  padding: '6px',
                  fontSize: `${xSmallFontSize}px`,
                  border: 'none',
                  borderRadius: '4px',
                  cursor:
                    checkedDocuments.length === 0
                      ? 'not-allowed'
                      : 'pointer',
                  backgroundColor:
                    checkedDocuments.length === 0
                      ? theme.colors.bgSecondary
                      : theme.colors.primary,
                  color: theme.colors.textWhite,
                  opacity: checkedDocuments.length === 0 ? 0.5 : 1,
                }}
              >
                Move
              </button>
              <button
                onClick={handleDeleteDocuments}
                disabled={checkedDocuments.length === 0}
                style={{
                  flex: 1,
                  padding: '6px',
                  fontSize: `${xSmallFontSize}px`,
                  border: 'none',
                  borderRadius: '4px',
                  cursor:
                    checkedDocuments.length === 0
                      ? 'not-allowed'
                      : 'pointer',
                  backgroundColor:
                    checkedDocuments.length === 0
                      ? theme.colors.bgSecondary
                      : theme.colors.danger,
                  color: theme.colors.textWhite,
                  opacity: checkedDocuments.length === 0 ? 0.5 : 1,
                }}
              >
                Delete
              </button>
              </>
)}
          </div>
        </div>
      </div>
    </div>
    </div> 
  );
};
export default ViewDeposition;